import { getActiveTenantKey, getActiveUserId, getUserScopedKey } from '../offline/tenant'
import { loadJSON, saveJSON } from './storage'
import type { WidgetLayout } from './dashboardStorage'

/**
 * Which Panel widgets a specific user has chosen to feature on their own
 * /home landing page, and how they've personally arranged them there.
 *
 * Deliberately separate from `dashboardStorage.ts`'s `PivotWidgetV1` (the
 * widget's own definition — its report, pivot config, chart type — stays
 * tenant-wide, defined once in Panel). What's personal is only the
 * *selection* and *arrangement*: two operators at the same tenant can
 * feature entirely different Panel widgets on their own Home, or the same
 * ones arranged differently, without affecting each other or Panel itself.
 */
export type HomeWidgetPinV1 = {
  widgetId: string
  layout?: WidgetLayout
}

type HomeWidgetsPayloadV1 = {
  version: 1
  pins: HomeWidgetPinV1[]
}

const HOME_WIDGETS_STORAGE_KEY = 'home-widgets:v1'

const homeWidgetsKey = (tenantKey: string, userId: string): string => (
  getUserScopedKey(HOME_WIDGETS_STORAGE_KEY, tenantKey, userId)
)

const isWidgetLayout = (value: unknown): value is WidgetLayout => {
  if (!value || typeof value !== 'object') return false
  const record = value as Record<string, unknown>
  return typeof record.x === 'number' && Number.isFinite(record.x) && record.x >= 0 &&
    typeof record.y === 'number' && Number.isFinite(record.y) && record.y >= 0 &&
    typeof record.w === 'number' && Number.isFinite(record.w) && record.w >= 1 &&
    typeof record.h === 'number' && Number.isFinite(record.h) && record.h >= 1
}

const normalizePin = (candidate: unknown): HomeWidgetPinV1 | null => {
  if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return null
  const record = candidate as Record<string, unknown>
  const widgetId = typeof record.widgetId === 'string' ? record.widgetId.trim() : ''
  if (!widgetId) return null
  const layout = isWidgetLayout(record.layout) ? record.layout : undefined
  return { widgetId, layout }
}

export const loadHomeWidgetPins = (
  tenantKey = getActiveTenantKey(),
  userId = getActiveUserId()
): HomeWidgetPinV1[] => {
  const payload = loadJSON<HomeWidgetsPayloadV1>(homeWidgetsKey(tenantKey, userId), { version: 1, pins: [] })
  return (payload.pins ?? [])
    .map(normalizePin)
    .filter((pin): pin is HomeWidgetPinV1 => Boolean(pin))
}

const persistPins = (pins: HomeWidgetPinV1[], tenantKey: string, userId: string): void => {
  saveJSON<HomeWidgetsPayloadV1>(homeWidgetsKey(tenantKey, userId), { version: 1, pins })
}

export const isHomeWidgetPinned = (
  widgetId: string,
  tenantKey = getActiveTenantKey(),
  userId = getActiveUserId()
): boolean => (
  loadHomeWidgetPins(tenantKey, userId).some((pin) => pin.widgetId === widgetId)
)

export const setHomeWidgetPinned = (
  widgetId: string,
  pinned: boolean,
  tenantKey = getActiveTenantKey(),
  userId = getActiveUserId()
): HomeWidgetPinV1[] => {
  const existing = loadHomeWidgetPins(tenantKey, userId)
  const next = pinned
    ? (existing.some((pin) => pin.widgetId === widgetId) ? existing : [...existing, { widgetId }])
    : existing.filter((pin) => pin.widgetId !== widgetId)
  persistPins(next, tenantKey, userId)
  return next
}

// v2.2 findings.md #48: separate key (not a field on the pins payload) so
// none of the existing pin writers can drop it by accident.
const HOME_DEFAULTS_SEEDED_KEY = 'home-widgets:defaults-seeded:v1'

/**
 * Give a user a starting set of Home widgets exactly once. Runs the first
 * time this user's Home (or Panel) loads after the feature exists:
 * - a user with **no pins** gets `defaultWidgetIds` (only those whose widget
 *   actually exists — see `availableWidgetIds`; a pin to a missing widget
 *   would just be silently hidden anyway);
 * - a user who **already pinned something** is left exactly as they are.
 * Either way the user is then marked as done, so un-pinning every default
 * later never brings them back. Returns the pins after the call.
 */
export const seedDefaultHomeWidgetPinsIfNeeded = (
  defaultWidgetIds: readonly string[],
  availableWidgetIds: ReadonlySet<string>,
  tenantKey = getActiveTenantKey(),
  userId = getActiveUserId()
): HomeWidgetPinV1[] => {
  const existing = loadHomeWidgetPins(tenantKey, userId)
  const flagKey = getUserScopedKey(HOME_DEFAULTS_SEEDED_KEY, tenantKey, userId)
  if (loadJSON<boolean>(flagKey, false) === true) return existing

  let next = existing
  if (existing.length === 0) {
    next = defaultWidgetIds
      .filter((widgetId) => availableWidgetIds.has(widgetId))
      .map((widgetId) => ({ widgetId }))
    persistPins(next, tenantKey, userId)
  }
  saveJSON<boolean>(flagKey, true)
  return next
}

// Mirrors `dashboardStorage.ts`'s `setWidgetLayouts` — persists every
// changed entry from one drag/resize gesture in a single batch, since
// react-grid-layout's stop callbacks always hand back the full layout (the
// compactor may have shifted other widgets too).
export const setHomeWidgetLayouts = (
  entries: Array<{ id: string; layout: WidgetLayout }>,
  tenantKey = getActiveTenantKey(),
  userId = getActiveUserId()
): HomeWidgetPinV1[] => {
  const layoutById = new Map(entries.map((entry) => [entry.id, entry.layout] as const))
  const existing = loadHomeWidgetPins(tenantKey, userId)
  const next = existing.map((pin) => {
    const layout = layoutById.get(pin.widgetId)
    return layout ? { ...pin, layout } : pin
  })
  persistPins(next, tenantKey, userId)
  return next
}
